<template>
  <VAvatar
    ref="baseAvatar"
    class="base-avatar"
    :color="placeholderBackgroundColor ?? 'neutral-500'"
    :size="size"
  >
    <slot name="default">
      <VImg
        :src="imgSrc"
        :alt="alt"
        cover
      >
        <template #placeholder>
          <div
            v-if="useInitials"
            :class="[
              'd-flex align-center justify-center fill-height prevent-select-text font-weight-semibold',
            ]"
            data-testid="initials"
          >
            <span class="content-size">{{ initials }}</span>
          </div>
          <div
            v-else
            class="d-flex align-center justify-center fill-height"
          >
            <VIcon
              :color="iconColor"
              :icon="icon"
              :size="contentSize"
            />
          </div>
        </template>
      </VImg>
    </slot>
  </VAvatar>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Alternative text for the avatar image. */
    alt?: string;
    /** Source URL for the avatar image. */
    src?: string | null | undefined;
    /** Size of the avatar in pixels. */
    size?: number | string;
    /** Icon to display when no image is provided and useInitials is false. */
    icon?: string;
    /** User's full name for generating initials. */
    userName?: string;
    /** Custom background color for the placeholder. */
    placeholderBackgroundColor?: string;
    /** Color of the icon when displayed. */
    iconColor?: string;
    /** Whether to display user initials instead of an icon when no image is provided. */
    useInitials?: boolean;

  }>(),
  {
    alt: '',
    icon: 'person',
    iconColor: 'white',
    src: null,
    size: 40,
    userName: '',
    useInitials: false,
    placeholderBackgroundColor: undefined,
  },
);

/**
 * Computed property that returns the image source or undefined
 */
const imgSrc = computed(() => props.src ?? undefined);

const twThemeFontSizeMap = {
  64: 24,
  48: 16,
  40: 16,
  32: 12,
  24: 11,
  20: 10,
  16: 8,
  12: 8,
};

const twThemeIconSizeMap = {
  64: 32,
  48: 24,
  40: 24,
  32: 16,
  24: 12,
  20: 12,
  16: 8,
  12: 8,
};

/**
 * Computed property that determines the size of the content
 */
const contentSize = computed(() => {
  const sizeInt
    = typeof props.size === 'string' ? parseInt(props.size) : props.size;
  let size = Math.round(sizeInt * 0.5);

  if (props.useInitials && sizeInt in twThemeFontSizeMap) {
    size = twThemeFontSizeMap[sizeInt as keyof typeof twThemeFontSizeMap];
  }

  if (!props.useInitials && sizeInt in twThemeIconSizeMap) {
    size = twThemeIconSizeMap[sizeInt as keyof typeof twThemeIconSizeMap];
  }

  return size;
});

/**
 * Computed property that generates initials from the user's name
 * Takes up to two words and uses their first letters
 */

const initials = computed(() => {
  const sizeInt = typeof props.size === 'string' ? parseInt(props.size, 10) : props.size;
  const name = (props.userName).trim().toUpperCase();
  if (sizeInt <= 12) {
    return name.charAt(0);
  }

  const initials = name.split(' ').map((word) => word.charAt(0));
  const firstInitial = initials[0] ?? '';
  const lastInitial = initials[initials.length - 1] ?? '';
  return firstInitial + lastInitial;
});
</script>

<style scoped lang="scss">
.content-size {
  font-size: v-bind('contentSize + "px"') !important;
}
</style>
